/**
 * Price Change Engine
 *
 * Calculates expected points and price change probabilities for F1 Fantasy
 * Budget Builder feature.
 *
 * NOTE: For PPM-based price change calculation (preferred), use ppm-price-change.ts.
 * This file provides utility functions and types used alongside the PPM calculator.
 *
 * Based on the official F1 Fantasy rules:
 * - Price changes are influenced by points scored in recent races
 * - Tier A (>= $19M): Smaller price swings (-0.3, -0.1, +0.1, +0.3)
 * - Tier B (< $19M): Larger price swings (-0.6, -0.5, -0.2, 0.0, +0.2, +0.6)
 */

// Import constants from single source of truth
import {
  applyPriceFloorToTierBProbabilities,
  determineTier as determineTierFromConstants,
  PPM_THRESHOLDS,
  type PriceChangeColumn,
  TIER_A_COLUMNS,
  TIER_B_COLUMNS,
  TIER_B_POOR_THRESHOLD,
  type TierAColumn,
  type TierBColumn,
} from "./constants";

// ============================================================================
// Normal Distribution Helpers
// ============================================================================

/**
 * Normal CDF approximation (Abramowitz and Stegun).
 * Returns P(X <= x) for a standard normal distribution.
 */
function normalCDF(x: number, mean: number, stddev: number): number {
  const z = (x - mean) / stddev;
  const t = 1 / (1 + 0.2316419 * Math.abs(z));
  const d = 0.3989423 * Math.exp((-z * z) / 2);
  const p =
    d *
    t *
    (0.3193815 +
      t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
  return z > 0 ? 1 - p : p;
}

/**
 * Calculate price change probabilities using the actual MC distribution.
 *
 * Instead of a static lookup table, uses the PPM thresholds adjusted for the
 * 3-race rolling average with the normal distribution to compute P(score >= threshold)
 * for each bucket. This gives accurate probabilities even when mean is far from threshold.
 *
 * The thresholds account for R-2 and R-1 points already scored:
 *   adjustedThreshold = N × PPM_THRESHOLD × currentPrice - ptsR2 - ptsR1
 * where N = races in the rolling window (1, 2, or 3).
 */
export function calculatePriceChangeProbabilitiesFromDistribution(
  mean: number,
  stdDev: number,
  currentPrice: number,
  tier: "A" | "B",
  pointsRMinus2?: number | null,
  pointsRMinus1?: number | null,
): PriceChangeProbabilities {
  if (stdDev <= 0 || currentPrice <= 0) {
    return calculatePriceChangeProbabilities(mean, tier);
  }

  const columns = tier === "A" ? TIER_A_COLUMNS : TIER_B_COLUMNS;

  // Account for 3-race rolling average: adjust thresholds based on historical points
  const hasR2 = pointsRMinus2 != null;
  const hasR1 = pointsRMinus1 != null;
  const n = 1 + (hasR1 ? 1 : 0) + (hasR2 ? 1 : 0);
  const historicalPoints = (pointsRMinus2 ?? 0) + (pointsRMinus1 ?? 0);

  // Points thresholds adjusted for the rolling window
  // Formula: N × PPM_THRESHOLD × currentPrice - historicalPoints
  const ppmThresholds =
    tier === "A"
      ? [PPM_THRESHOLDS.POOR, PPM_THRESHOLDS.GOOD, PPM_THRESHOLDS.GREAT]
      : [TIER_B_POOR_THRESHOLD, PPM_THRESHOLDS.GOOD, PPM_THRESHOLDS.GREAT];

  const thresholds = ppmThresholds.map(
    (ppm) => n * ppm * currentPrice - historicalPoints,
  );

  const probs: number[] = [];
  let cumulativeProb = 0;

  for (let i = 0; i < columns.length; i++) {
    let prob: number;
    if (i === 0) {
      prob = normalCDF(thresholds[0], mean, stdDev);
    } else if (i === columns.length - 1) {
      prob = 1 - normalCDF(thresholds[thresholds.length - 1], mean, stdDev);
    } else {
      prob =
        normalCDF(thresholds[i], mean, stdDev) -
        normalCDF(thresholds[i - 1], mean, stdDev);
    }
    probs.push(Math.max(0, prob));
    cumulativeProb += Math.max(0, prob);
  }

  if (cumulativeProb <= 0) {
    return calculatePriceChangeProbabilities(mean, tier);
  }

  const result: PriceChangeProbabilities = {};
  for (let i = 0; i < columns.length; i++) {
    // Cap individual probabilities at 1%–99% for predictions: no outcome is ever
    // truly certain (a DNF = -20 pts can always shift the rolling average).
    // Only completed races with actual data should show 0% or 100%.
    const raw = Math.round((probs[i] / cumulativeProb) * 100);
    result[columns[i]] = Math.max(1, Math.min(99, raw));
  }

  // Fix rounding to sum to 100
  const sum = Object.values(result).reduce((a, b) => a + b, 0);
  if (sum !== 100) {
    const maxKey = Object.entries(result).reduce((a, b) =>
      b[1] > a[1] ? b : a,
    )[0];
    result[maxKey] += 100 - sum;
  }

  return result;
}

// Re-export constants for backwards compatibility
export {
  TIER_A_COLUMNS,
  TIER_B_COLUMNS,
  type PriceChangeColumn,
  type TierAColumn,
  type TierBColumn,
};

export interface PriceChangeProbabilities {
  [column: string]: number; // Probability as percentage (0-100)
}

export interface DriverBudgetCalculation {
  driverId: string;
  driverCode: string;
  driverNumber: number | null; // Driver's car number (e.g., 1 for VER, 44 for HAM)
  driverName: string;
  constructorName: string;
  constructorColor: string;
  currentPrice: number;
  pointsRMinus2: number | null;
  pointsRMinus1: number | null;
  expectedPoints: number;
  priceChangeProbabilities: PriceChangeProbabilities;
  expectedPriceDelta: number;
  tier: "A" | "B";
}

export interface ConstructorBudgetCalculation {
  constructorId: string;
  constructorCode: string;
  constructorName: string;
  constructorColor: string;
  currentPrice: number;
  pointsRMinus2: number | null;
  pointsRMinus1: number | null;
  expectedPoints: number;
  priceChangeProbabilities: PriceChangeProbabilities;
  expectedPriceDelta: number;
  tier: "A" | "B";
}

// ============================================================================
// Core Calculation Functions
// ============================================================================

/**
 * Calculate expected points for the upcoming race based on recent performance
 *
 * Uses a weighted average:
 * - If both R-2 and R-1 are available: 40% R-2 + 60% R-1
 * - If only one is available: use that value
 * - If neither is available: return 0
 */
export function calculateExpectedPoints(
  pointsRMinus2: number | null,
  pointsRMinus1: number | null,
): number {
  if (pointsRMinus2 === null && pointsRMinus1 === null) {
    return 0;
  }

  if (pointsRMinus2 === null) {
    return pointsRMinus1!;
  }

  if (pointsRMinus1 === null) {
    return pointsRMinus2;
  }

  // Weighted average: 40% R-2, 60% R-1 (more weight on recent performance)
  const weighted = pointsRMinus2 * 0.4 + pointsRMinus1 * 0.6;
  return Math.round(weighted * 10) / 10; // Round to 1 decimal place
}

/**
 * Determine tier based on price
 * Uses the shared determineTier from constants.ts
 */
export const determineTier = determineTierFromConstants;

/**
 * Calculate price change probabilities for Tier A drivers/constructors
 *
 * Tier A has smaller price swings: -0.3, -0.1, +0.1, +0.3
 *
 * Thresholds (approximate, based on competition analysis):
 * - Very high performance (xPts >= 35): High chance of +0.3
 * - High performance (xPts >= 25): Moderate chance of +0.1 or +0.3
 * - Average performance (xPts >= 15): Mixed probabilities
 * - Low performance (xPts < 15): High chance of -0.1 or -0.3
 */
export function calculateTierAProbabilities(
  expectedPoints: number,
): PriceChangeProbabilities {
  // Define probability distributions based on expected points
  if (expectedPoints >= 40) {
    return { "-0.3": 0, "-0.1": 0, "+0.1": 10, "+0.3": 90 };
  }
  if (expectedPoints >= 35) {
    return { "-0.3": 0, "-0.1": 5, "+0.1": 20, "+0.3": 75 };
  }
  if (expectedPoints >= 30) {
    return { "-0.3": 5, "-0.1": 10, "+0.1": 35, "+0.3": 50 };
  }
  if (expectedPoints >= 25) {
    return { "-0.3": 10, "-0.1": 20, "+0.1": 45, "+0.3": 25 };
  }
  if (expectedPoints >= 20) {
    return { "-0.3": 15, "-0.1": 35, "+0.1": 35, "+0.3": 15 };
  }
  if (expectedPoints >= 15) {
    return { "-0.3": 25, "-0.1": 45, "+0.1": 25, "+0.3": 5 };
  }
  if (expectedPoints >= 10) {
    return { "-0.3": 40, "-0.1": 45, "+0.1": 12, "+0.3": 3 };
  }
  if (expectedPoints >= 5) {
    return { "-0.3": 55, "-0.1": 35, "+0.1": 8, "+0.3": 2 };
  }
  // Very low performance
  return { "-0.3": 70, "-0.1": 25, "+0.1": 4, "+0.3": 1 };
}

/**
 * Calculate price change probabilities for Tier B drivers/constructors
 *
 * Standard Tier B buckets: -0.6 (terrible), -0.2 (poor), +0.2 (good), +0.6 (great)
 * Edge-case buckets (e.g. "0.0" for floor-constrained drivers, partial drops like
 * "-0.4") are added downstream by applyPriceFloorToTierBProbabilities.
 */
export function calculateTierBProbabilities(
  expectedPoints: number,
): PriceChangeProbabilities {
  if (expectedPoints >= 30) {
    return { "-0.6": 0, "-0.2": 0, "+0.2": 20, "+0.6": 80 };
  }
  if (expectedPoints >= 25) {
    return { "-0.6": 0, "-0.2": 5, "+0.2": 30, "+0.6": 65 };
  }
  if (expectedPoints >= 20) {
    return { "-0.6": 0, "-0.2": 15, "+0.2": 45, "+0.6": 40 };
  }
  if (expectedPoints >= 15) {
    return { "-0.6": 5, "-0.2": 20, "+0.2": 45, "+0.6": 30 };
  }
  if (expectedPoints >= 10) {
    return { "-0.6": 15, "-0.2": 40, "+0.2": 35, "+0.6": 10 };
  }
  if (expectedPoints >= 5) {
    return { "-0.6": 35, "-0.2": 40, "+0.2": 20, "+0.6": 5 };
  }
  if (expectedPoints >= 0) {
    return { "-0.6": 55, "-0.2": 30, "+0.2": 12, "+0.6": 3 };
  }
  // Negative expected points (DNF, DQ, etc.)
  return { "-0.6": 75, "-0.2": 20, "+0.2": 4, "+0.6": 1 };
}

/**
 * Calculate price change probabilities based on tier
 */
export function calculatePriceChangeProbabilities(
  expectedPoints: number,
  tier: "A" | "B",
): PriceChangeProbabilities {
  if (tier === "A") {
    return calculateTierAProbabilities(expectedPoints);
  }
  return calculateTierBProbabilities(expectedPoints);
}

/**
 * Calculate expected price delta from probability distribution
 *
 * xΔ$ = Σ (price_change_i × probability_i / 100)
 */
export function calculateExpectedPriceDelta(
  probabilities: PriceChangeProbabilities,
): number {
  let expectedDelta = 0;

  for (const [change, probability] of Object.entries(probabilities)) {
    const changeValue = Number.parseFloat(change);
    expectedDelta += changeValue * (probability / 100);
  }

  // Round to 2 decimal places
  return Math.round(expectedDelta * 100) / 100;
}

// ============================================================================
// Main Calculation Functions
// ============================================================================

/**
 * Calculate full budget builder data for a driver
 */
export function calculateDriverBudget(
  driver: {
    driverId: string;
    driverCode: string;
    driverNumber?: number | null;
    driverName: string;
    constructorName: string;
    constructorColor: string;
    currentPrice: number;
    pointsRMinus2: number | null;
    pointsRMinus1: number | null;
    tier: "A" | "B";
  },
  priceFloor: number,
): DriverBudgetCalculation {
  const expectedPoints = calculateExpectedPoints(
    driver.pointsRMinus2,
    driver.pointsRMinus1,
  );

  const rawProbabilities = calculatePriceChangeProbabilities(
    expectedPoints,
    driver.tier,
  );
  const priceChangeProbabilities =
    driver.tier === "B"
      ? applyPriceFloorToTierBProbabilities(
          driver.currentPrice,
          rawProbabilities,
          priceFloor,
        )
      : rawProbabilities;

  const expectedPriceDelta = calculateExpectedPriceDelta(
    priceChangeProbabilities,
  );

  return {
    ...driver,
    driverNumber: driver.driverNumber ?? null,
    expectedPoints,
    priceChangeProbabilities,
    expectedPriceDelta,
  };
}

/**
 * Calculate full budget builder data for a constructor
 */
export function calculateConstructorBudget(
  team: {
    constructorId: string;
    constructorCode: string;
    constructorName: string;
    constructorColor: string;
    currentPrice: number;
    pointsRMinus2: number | null;
    pointsRMinus1: number | null;
    tier: "A" | "B";
  },
  priceFloor: number,
): ConstructorBudgetCalculation {
  const expectedPoints = calculateExpectedPoints(
    team.pointsRMinus2,
    team.pointsRMinus1,
  );

  const rawProbabilities = calculatePriceChangeProbabilities(
    expectedPoints,
    team.tier,
  );
  const priceChangeProbabilities =
    team.tier === "B"
      ? applyPriceFloorToTierBProbabilities(
          team.currentPrice,
          rawProbabilities,
          priceFloor,
        )
      : rawProbabilities;

  const expectedPriceDelta = calculateExpectedPriceDelta(
    priceChangeProbabilities,
  );

  return {
    ...team,
    expectedPoints,
    priceChangeProbabilities,
    expectedPriceDelta,
  };
}

/**
 * Get probability columns for a tier
 */
export function getProbabilityColumns(tier: "A" | "B"): readonly string[] {
  return tier === "A" ? TIER_A_COLUMNS : TIER_B_COLUMNS;
}

/**
 * Create probabilities for a completed race (100% for actual price change bucket)
 *
 * For completed races, we know the actual price change, so we show 100% probability
 * for the bucket that matches the actual change.
 */
export function createCompletedRaceProbabilities(
  actualPriceChangeBucket: string,
  tier: "A" | "B",
): PriceChangeProbabilities {
  const columns = tier === "A" ? TIER_A_COLUMNS : TIER_B_COLUMNS;

  if ((columns as readonly string[]).includes(actualPriceChangeBucket)) {
    const probabilities: PriceChangeProbabilities = {};
    for (const col of columns) {
      probabilities[col] = col === actualPriceChangeBucket ? 100 : 0;
    }
    return probabilities;
  }

  // Non-standard bucket (e.g. floor-clipped "-0.4", "-0.5"): replace the nearest standard
  // column with the actual bucket, keeping other 3 standard columns at 0% — always 4 keys.
  const actualValue = parseFloat(actualPriceChangeBucket);
  const standardCols = [...columns] as string[];
  let nearestCol = standardCols[0];
  let nearestDist = Math.abs(parseFloat(standardCols[0]) - actualValue);
  for (const col of standardCols.slice(1)) {
    const dist = Math.abs(parseFloat(col) - actualValue);
    if (dist < nearestDist) {
      nearestDist = dist;
      nearestCol = col;
    }
  }
  const probabilities: PriceChangeProbabilities = {};
  for (const col of standardCols) {
    if (col === nearestCol) {
      probabilities[actualPriceChangeBucket] = 100;
    } else {
      probabilities[col] = 0;
    }
  }
  return probabilities;
}

/**
 * Calculate driver budget with support for completed races and stage-adjusted predictions
 *
 * If the race is completed (isCompleted = true), uses actual price change data
 * instead of predictions.
 *
 * If overrideExpectedPoints is provided, uses that value instead of calculating
 * from historical data (used for stage-adjusted predictions).
 */
export function calculateDriverBudgetWithActual(
  driver: {
    driverId: string;
    driverCode: string;
    driverNumber?: number | null; // Driver's car number (e.g., 1 for VER, 44 for HAM)
    driverName: string;
    constructorName: string;
    constructorColor: string;
    currentPrice: number;
    pointsRMinus2: number | null;
    pointsRMinus1: number | null;
    tier: "A" | "B";
    // Override expected points with stage-adjusted value
    overrideExpectedPoints?: number;
    // New fields for completed races
    currentRacePoints?: number | null;
    actualPriceChange?: number | null;
    actualPriceChangeBucket?: string | null;
    isCompleted?: boolean;
  },
  priceFloor: number,
): DriverBudgetCalculation & {
  isCompleted: boolean;
  actualPriceChange: number | null;
} {
  // For completed races, use actual data
  if (
    driver.isCompleted &&
    driver.actualPriceChange !== null &&
    driver.actualPriceChange !== undefined &&
    driver.actualPriceChangeBucket
  ) {
    const priceChangeProbabilities = createCompletedRaceProbabilities(
      driver.actualPriceChangeBucket,
      driver.tier,
    );

    return {
      driverId: driver.driverId,
      driverCode: driver.driverCode,
      driverNumber: driver.driverNumber ?? null,
      driverName: driver.driverName,
      constructorName: driver.constructorName,
      constructorColor: driver.constructorColor,
      currentPrice: driver.currentPrice,
      pointsRMinus2: driver.pointsRMinus2,
      pointsRMinus1: driver.pointsRMinus1,
      // Use actual current race points if available
      expectedPoints: driver.currentRacePoints ?? 0,
      priceChangeProbabilities,
      // Use actual price change
      expectedPriceDelta: driver.actualPriceChange,
      tier: driver.tier,
      isCompleted: true,
      actualPriceChange: driver.actualPriceChange,
    };
  }

  // For incomplete races, use prediction (with optional override)
  const expectedPoints =
    driver.overrideExpectedPoints !== undefined
      ? driver.overrideExpectedPoints
      : calculateExpectedPoints(driver.pointsRMinus2, driver.pointsRMinus1);

  const rawProbabilities = calculatePriceChangeProbabilities(
    expectedPoints,
    driver.tier,
  );
  const priceChangeProbabilities =
    driver.tier === "B"
      ? applyPriceFloorToTierBProbabilities(
          driver.currentPrice,
          rawProbabilities,
          priceFloor,
        )
      : rawProbabilities;

  const expectedPriceDelta = calculateExpectedPriceDelta(
    priceChangeProbabilities,
  );

  return {
    driverId: driver.driverId,
    driverCode: driver.driverCode,
    driverNumber: driver.driverNumber ?? null,
    driverName: driver.driverName,
    constructorName: driver.constructorName,
    constructorColor: driver.constructorColor,
    currentPrice: driver.currentPrice,
    pointsRMinus2: driver.pointsRMinus2,
    pointsRMinus1: driver.pointsRMinus1,
    expectedPoints,
    priceChangeProbabilities,
    expectedPriceDelta,
    tier: driver.tier,
    isCompleted: false,
    actualPriceChange: null,
  };
}

/**
 * Calculate constructor budget with support for completed races and stage-adjusted predictions
 *
 * If overrideExpectedPoints is provided, uses that value instead of calculating
 * from historical data (used for stage-adjusted predictions).
 */
export function calculateConstructorBudgetWithActual(
  team: {
    constructorId: string;
    constructorCode: string;
    constructorName: string;
    constructorColor: string;
    currentPrice: number;
    pointsRMinus2: number | null;
    pointsRMinus1: number | null;
    tier: "A" | "B";
    // Override expected points with stage-adjusted value
    overrideExpectedPoints?: number;
    // New fields for completed races
    currentRacePoints?: number | null;
    actualPriceChange?: number | null;
    actualPriceChangeBucket?: string | null;
    isCompleted?: boolean;
  },
  priceFloor: number,
): ConstructorBudgetCalculation & {
  isCompleted: boolean;
  actualPriceChange: number | null;
} {
  // For completed races, use actual data
  if (
    team.isCompleted &&
    team.actualPriceChange !== null &&
    team.actualPriceChange !== undefined &&
    team.actualPriceChangeBucket
  ) {
    const priceChangeProbabilities = createCompletedRaceProbabilities(
      team.actualPriceChangeBucket,
      team.tier,
    );

    return {
      constructorId: team.constructorId,
      constructorCode: team.constructorCode,
      constructorName: team.constructorName,
      constructorColor: team.constructorColor,
      currentPrice: team.currentPrice,
      pointsRMinus2: team.pointsRMinus2,
      pointsRMinus1: team.pointsRMinus1,
      // Use actual current race points if available
      expectedPoints: team.currentRacePoints ?? 0,
      priceChangeProbabilities,
      // Use actual price change
      expectedPriceDelta: team.actualPriceChange,
      tier: team.tier,
      isCompleted: true,
      actualPriceChange: team.actualPriceChange,
    };
  }

  // For incomplete races, use prediction (with optional override)
  const expectedPoints =
    team.overrideExpectedPoints !== undefined
      ? team.overrideExpectedPoints
      : calculateExpectedPoints(team.pointsRMinus2, team.pointsRMinus1);

  const rawProbabilities = calculatePriceChangeProbabilities(
    expectedPoints,
    team.tier,
  );
  const priceChangeProbabilities =
    team.tier === "B"
      ? applyPriceFloorToTierBProbabilities(
          team.currentPrice,
          rawProbabilities,
          priceFloor,
        )
      : rawProbabilities;

  const expectedPriceDelta = calculateExpectedPriceDelta(
    priceChangeProbabilities,
  );

  return {
    constructorId: team.constructorId,
    constructorCode: team.constructorCode,
    constructorName: team.constructorName,
    constructorColor: team.constructorColor,
    currentPrice: team.currentPrice,
    pointsRMinus2: team.pointsRMinus2,
    pointsRMinus1: team.pointsRMinus1,
    expectedPoints,
    priceChangeProbabilities,
    expectedPriceDelta,
    tier: team.tier,
    isCompleted: false,
    actualPriceChange: null,
  };
}

/**
 * Sort drivers by expected price delta (descending - best growth first)
 */
export function sortByExpectedDelta<T extends { expectedPriceDelta: number }>(
  items: T[],
  ascending = false,
): T[] {
  return [...items].sort((a, b) =>
    ascending
      ? a.expectedPriceDelta - b.expectedPriceDelta
      : b.expectedPriceDelta - a.expectedPriceDelta,
  );
}

/**
 * Filter items by tier
 */
export function filterByTier<T extends { tier: "A" | "B" }>(
  items: T[],
  tier: "A" | "B",
): T[] {
  return items.filter((item) => item.tier === tier);
}

/**
 * Filter items by search query (supports multi-asset queries like "VER+NOR")
 */
export function filterBySearch<
  T extends {
    driverCode?: string;
    driverName?: string;
    constructorCode?: string;
    constructorName?: string;
  },
>(items: T[], query: string): T[] {
  if (!query.trim()) return items;

  const searchTerms = query
    .toUpperCase()
    .split("+")
    .map((t) => t.trim())
    .filter(Boolean);

  if (searchTerms.length === 0) return items;

  return items.filter((item) =>
    searchTerms.some((term) => {
      const code = item.driverCode || item.constructorCode || "";
      const name = item.driverName || item.constructorName || "";
      return (
        code.toUpperCase().includes(term) || name.toUpperCase().includes(term)
      );
    }),
  );
}
